import { useRef, useState } from 'react';
import { randomId } from '@shared/utils/randomId.js';
import Spinner from '@/components/Spinner';
import LexicalInput from '@/components/lexicalInput/LexicalInput';
import { PiCaretDownLight } from '@/components/react-icons/main';
import Input from '@/components/Input';
import IconV2 from './IconV2';
import { TbCircles } from '../react-icons/main/index.esm';
import attributeDataTypeOptions from '@/screens/apps/schema/attributeDataTypeOptions';
import { dataTypeMap } from '@shared/schema/dataTypeMap.js';
import FloatingSelectV2 from './FloatingSelectV2.jsx';
import useExternalUpdate from '@/hooks/useExternalUpdate';

const toggleOptions = [
  { label: 'Enabled', value: true },
  { label: 'Disabled', value: false }
];

const RightPaneFieldV2 = ({ key, type = 'select', value, nullable, nullableLabel, noun, icon, options, suggestions, placeholder, multiple, iconPosition, formattedValue, useSearch = true, selectCaret, selectStyles, onChange, onClick, onCancel, children, style, inputStyle, loading = false, autoComplete, useNumbers = true, dataType, useDataTypes, debounce, className = '', actions = [], disabled = false, handleSelectDataTypeChange, borderless = true, showLexicalActions = false, autoSize, allowMultiline, allowSelfOutput = false, dataArgumentKey }) => {
  const id = useRef(randomId());
  const fieldRef = useRef(null);
  const inputRef = useRef(null);
  const dataTypesRef = useRef(null);
  const isSelect = type === 'select';
  const isInput = type === 'input' || type === 'number' || type === 'password' || type === 'textarea';
  const isToggle = type === 'toggle';
  const isLexical = type === 'lexical';
  const isButton = type === 'button';
  const isDisabled = type === 'disabled';
  const isFalse = isToggle && value === false;
  const isTrue = isToggle && value === true;

  const [updateKey, setUpdateKey] = useState(randomId());

  useExternalUpdate({
    key: `lexicalInput-${key}`,
    callback: async () => {
      setUpdateKey(Math.random());
    }
  });

  const primitiveOptions = attributeDataTypeOptions?.filter((n) => n.value !== 'array' && n.value !== 'object')?.map((n) => ({ ...n, dataType: 'array' }));
  const dataTypeOptions = attributeDataTypeOptions?.map((n) => {
    if (n.value !== 'array') {
      return n;
    }

    return { ...n, children: [{ label: 'Any', value: 'array' }, ...primitiveOptions] };
  });

  options = options || (isToggle ? toggleOptions : options);
  value = isToggle ? (value === undefined ? false : value) : value;
  const activeOption = options?.find((n) => n?.value === value);

  if (activeOption?.icon) {
    icon = activeOption?.icon;
  }

  let valueLabel = isToggle ? toggleOptions.find((n) => n?.value === value)?.label : value;
  valueLabel = options?.length ? activeOption?.label : valueLabel;
  valueLabel = formattedValue || valueLabel;

  let finalLabel = valueLabel || placeholder || '';
  if (type === 'password') {
    finalLabel = (valueLabel || placeholder || '').replace(/./g, '•');
  }

  if (multiple && Array.isArray(value) && value?.length > 1 && !formattedValue) {
    const s = value?.length === 1 ? '' : 's';
    finalLabel = `${value?.length} ${noun || 'item'}${s}`;
  } else if (multiple && Array.isArray(value) && value?.length === 1 && !formattedValue) {
    const match = options?.find((n) => n.value === value[0]);
    finalLabel = `${match?.label || placeholder}`;
  }

  const optionTooltip = type === 'select' && activeOption ? activeOption?.description || '' : '';

  const handleClick = () => {
    if (disabled) {
      return;
    }
    if (isToggle) {
      onChange?.(!value);
    }
    if (isButton) {
      onClick?.();
    }
  };

  const handleChange = (value) => {
    if (disabled) {
      return;
    }
    onChange?.(value);
  };

  const handleSelectChange = (value) => {
    if (disabled) {
      return;
    }
    const val = Array.isArray(value) ? value : value?.value;
    onChange?.(val);
  };

  const handleKeyDown = (e) => {
    if (e.shiftKey && e.key === 'Enter') {
      e.preventDefault();
      e.stopPropagation();
      const currentValue = e.target.value;
      const selectionStart = e.target.selectionStart;
      const selectionEnd = e.target.selectionEnd;
      const newValue = currentValue.substring(0, selectionStart) + '\n' + currentValue.substring(selectionEnd);
      if (selectionStart !== undefined && selectionEnd !== undefined) {
        requestAnimationFrame(() => {
          if (inputRef.current) {
            inputRef.current.selectionStart = selectionStart + 1;
            inputRef.current.selectionEnd = selectionStart + 1;
            inputRef.current.focus();
          }
        });
      }
      handleChange(newValue);
    }
  };

  return (
    <div ref={fieldRef} className={`comp-right-pane-field-v2 ${id.current} ${type === 'custom' ? 'custom' : ''} ${isDisabled ? 'disabled' : ''} ${isFalse ? 'false' : ''} ${isTrue ? 'true' : ''} ${className}`} tabIndex={-1} data-argument-key={dataArgumentKey} style={{ ...style }}>
      {loading && <IconV2 icon={Spinner} />}
      {isInput && <Input className={borderless ? 'borderless' : ''} id={id.current} disabled={disabled} value={value} icon={icon} placeholder={placeholder} type={type} onChange={(e) => handleChange(e.target.value)} suggestions={suggestions} onCancel={onCancel} autoComplete={autoComplete} actions={actions} autoSize={autoSize} ref={inputRef} onKeyDown={handleKeyDown} allowMultiline={allowMultiline} />}
      {isLexical && <LexicalInput key={updateKey} id={id.current} disabled={disabled} value={value} inputStyle={inputStyle} actions={[]} debounce={debounce} onDebounce={handleChange} onChange={handleChange} placeholder={placeholder} variation="workflow" useDataTypes={useDataTypes} dataType={dataType} showActions={showLexicalActions} suggestions={suggestions} allowSelfOutput={allowSelfOutput} />}
      {type === 'custom' && children}
      {(isSelect || isToggle || isButton) && (
        <>
          <div className={'right-pane-select-v2'} onClick={handleClick} style={selectStyles} data-title={optionTooltip}>
            {dataType && useDataTypes && (
              <div style={{ marginLeft: '8px' }} className={`right-pane-select-v2-data-type`}>
                <span>{dataTypeMap[dataType]}</span>
              </div>
            )}

            {iconPosition !== 'left' && !useDataTypes && <IconV2 icon={icon} />}
            <div className={`label ${placeholder && !value ? 'placeholder' : ''}`}>{finalLabel}</div>
            {type === 'select' && selectCaret && !useDataTypes && <IconV2 icon={PiCaretDownLight} className="caret" />}

            {useDataTypes && typeof handleSelectDataTypeChange === 'function' && (
              <div style={{ marginLeft: 'auto' }} className="right-pane-select-v2-actions" ref={dataTypesRef}>
                <IconV2 icon={TbCircles} variant={value?.dataType || dataType ? 'medium' : 'inactive'} title={'Change Argument Data Type'} />
                {!disabled && <FloatingSelectV2 triggers={[dataTypesRef]} nullable={true} nullableLabel={nullableLabel} value={value?.dataType || dataType} options={dataTypeOptions} onChange={handleSelectDataTypeChange} useSearch={false} />}
              </div>
            )}
          </div>
          {options?.length > 0 && !disabled && !isToggle && !isButton && <FloatingSelectV2 triggers={[fieldRef]} nullable={nullable} nullableLabel={nullableLabel} value={value} options={options} multiple={multiple} onChange={handleSelectChange} useNumbers={options?.length < 10 && !!useNumbers} useSearch={useSearch} />}
        </>
      )}
    </div>
  );
};

export default RightPaneFieldV2;
